import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { DRAWER_WIDTH, useDrawer } from '@app/src/components/Drawer/DrawerContext'
import EmptyState, { EmptyStateVariant } from '@app/src/components/EmptyState/EmptyState'
import { getOrganizationDisplayName } from '@app/src/components/Table/Cells/CompanyCell'
import StatisticsCard from '@app/src/pages/Dashboards/StatisticsCard'
import DrawerViewFindings from '@app/src/pages/Findings/DrawerViewFindings'
import type { FindingsListRowModel } from '@app/src/pages/Findings/FindingsList/FindingsListScene'
import {
  type AdverseScreeningQuestionnaire,
  getScreeningColumns,
  getScreeningLabelId,
  getScreeningSlugByTitle,
  getScreeningTemplateIds,
  toFindingsListRow,
} from '@app/src/pages/Findings/findingsUtils'
import { useScreeningTemplates } from '@app/src/pages/Findings/useFindingsView'
import { Operators, RESPONSE_ITEM_LATEST_SUBMITTED_FILTER } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { BenchmarkData } from '@app/src/pages/SustainabilityProfile/benchmark/types'
import {
  PROFILE_INTEGRITY_SCREENING_QUESTIONNAIRES,
  type ProviderScreeningResult,
} from '@app/src/pages/SustainabilityProfile/useProviderFindings'
import type { QuestionnaireTemplate, ResponseItem } from '@app/src/types/resourceExplorer'
import { ScrapingType } from '@app/src/types/resourceExplorer'
import type { SvgIconComponent } from '@mui/icons-material'
import { ArticleOutlined, GppMaybeOutlined, ListOutlined, PersonOutlineOutlined } from '@mui/icons-material'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

export const INTEGRITY_SECTION_ID = 'sustainability-profile-integrity'

interface IntegrityScreeningSectionProps {
  providerId: number
  screenings: ProviderScreeningResult[]
  benchmark?: BenchmarkData
  isLoading: boolean
  isScreening?: boolean
}

// Only the screening types that are part of the Sustainability Profile have an icon here —
// see PROFILE_INTEGRITY_SCREENING_QUESTIONNAIRES. Lookups fall back to ArticleOutlined.
const SCREENING_ICON: Record<string, SvgIconComponent> = {
  'adverse-media': ArticleOutlined,
  sanctions: GppMaybeOutlined,
  pep: PersonOutlineOutlined,
}

const FINDINGS_PAGE_SIZE = 100

const ScreeningCard: React.FC<{
  result: ProviderScreeningResult
  benchmark?: BenchmarkData
  selected: boolean
  onSelect?: () => void
  isScreening?: boolean
}> = ({ result, benchmark, selected, onSelect, isScreening = false }) => {
  const { formatMessage, formatNumber } = useIntl()
  const Icon = SCREENING_ICON[result.column.type] ?? ArticleOutlined
  const peerMean = benchmark?.screenings[result.column.type]?.mean

  const loading = !result.isScreened && isScreening

  const caption = !result.isScreened
    ? formatMessage({ id: 'sustainabilityProfile.integrity.notScreened' })
    : peerMean !== undefined
      ? formatMessage({ id: 'sustainabilityProfile.integrity.peerMean' }, { mean: peerMean })
      : (result.column.description ?? '')

  return (
    <Box
      onClick={onSelect}
      role={onSelect ? 'button' : undefined}
      aria-pressed={onSelect ? selected : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onKeyDown={
        onSelect
          ? event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onSelect()
              }
            }
          : undefined
      }
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'transparent',
        transition: theme => theme.transitions.create(['border-color', 'box-shadow']),
        ...(onSelect && {
          cursor: 'pointer',
          '&:hover': { borderColor: selected ? 'primary.main' : 'grey.300' },
          '&:focus-visible': { outline: 'none', borderColor: 'primary.main' },
        }),
      }}
    >
      <StatisticsCard
        title={formatMessage({ id: result.column.labelId, defaultMessage: result.column.fallbackLabel })}
        justifyContent="left"
        titleAdornment={<Icon fontSize="small" color="action" />}
      >
        <Stack spacing={0.5} alignItems="flex-start" width="100%">
          {loading ? (
            <>
              <Skeleton variant="text" width={48} height={56} />
              <Skeleton variant="text" width="80%" />
            </>
          ) : (
            <>
              <Typography variant="h1" fontWeight={500} color={result.count > 0 ? 'semantic.error' : 'text.primary'}>
                {formatNumber(result.count)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {caption}
              </Typography>
            </>
          )}
        </Stack>
      </StatisticsCard>
    </Box>
  )
}

const FindingCard: React.FC<{ row: FindingsListRowModel; screeningTemplates: QuestionnaireTemplate[] }> = ({
  row,
  screeningTemplates,
}) => {
  const { formatMessage, formatDate } = useIntl()
  const { openDrawer } = useDrawer()

  const slug = getScreeningSlugByTitle(row.questionnaireTemplateTitle ?? '', screeningTemplates)
  const Icon = (slug ? SCREENING_ICON[slug] : undefined) ?? ArticleOutlined
  const categoryLabel = slug
    ? formatMessage({ id: getScreeningLabelId(slug), defaultMessage: row.questionnaireTemplateTitle ?? '' })
    : row.questionnaireTemplateTitle

  const responseItemId = row.responseItemId
  const onOpen =
    responseItemId !== null
      ? () =>
          openDrawer(<DrawerViewFindings provider={row.provider} responseItemId={responseItemId} />, {
            maxWidth: DRAWER_WIDTH.wide,
          })
      : undefined

  return (
    <Stack
      spacing={0.5}
      onClick={onOpen}
      sx={{
        p: 2,
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: theme => `1px solid ${theme.palette.grey[200]}`,
        ...(onOpen && {
          cursor: 'pointer',
          transition: theme => theme.transitions.create(['background-color', 'border-color']),
          '&:hover': { bgcolor: 'grey.50', borderColor: 'grey.300' },
        }),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
          <Icon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={600} noWrap>
            {getOrganizationDisplayName(row.provider)}
          </Typography>
          {categoryLabel && (
            <Typography variant="body2" color="text.secondary" noWrap>
              · {categoryLabel}
            </Typography>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" noWrap>
          {formatDate(row.date, { year: 'numeric', month: 'short', day: 'numeric' })}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" noWrap>
        {row.findingDisplayName}
      </Typography>
    </Stack>
  )
}

const FindingsList: React.FC<{ providerId: number; selectedType?: AdverseScreeningQuestionnaire }> = ({
  providerId,
  selectedType,
}) => {
  const { formatMessage } = useIntl()
  const { screeningTemplates, isLoading: templatesLoading } = useScreeningTemplates()

  // Greenwashing templates are always excluded here too, so a provider's Greenwashing findings never
  // leak into the "all" view of this list.
  const profileTemplateIds = useMemo(
    () =>
      getScreeningColumns(screeningTemplates)
        .filter(column => PROFILE_INTEGRITY_SCREENING_QUESTIONNAIRES.includes(column.type))
        .flatMap(column => column.templateIds),
    [screeningTemplates],
  )

  const templateIds = useMemo(
    () => (selectedType ? getScreeningTemplateIds(screeningTemplates, selectedType) : profileTemplateIds),
    [screeningTemplates, selectedType, profileTemplateIds],
  )

  const payload = useMemo(
    () => ({
      filter: [
        RESPONSE_ITEM_LATEST_SUBMITTED_FILTER,
        {
          name: 'response.request.template.scrapingType',
          filters: [{ operator: Operators.EqualTo, value: ScrapingType.AdverseScraping }],
        },
        { name: 'answerSearch', filters: [{ operator: Operators.In, value: ['Yes'] }] },
        {
          name: 'response.request.subscriptions.target.id',
          filters: [{ operator: Operators.EqualTo, value: providerId }],
        },
        {
          name: 'response.request.template.id',
          filters: [{ operator: Operators.In, value: templateIds.length ? templateIds : [-1] }],
        },
      ],
      include: ['requestItem.template', 'response.request.template', 'response.request.subscriptions.target'],
      pagination: { pageNumber: 1, itemsPerPage: FINDINGS_PAGE_SIZE },
    }),
    [providerId, templateIds],
  )

  const { items = [], isLoading } = useFetchCollectionWithPost<ResponseItem>({
    endpoint: endpoints.responseItemsCollection,
    key: [
      FetchKey.ResponseItemsCollection,
      'profile-findings',
      providerId,
      selectedType ?? 'all',
      templateIds.join(','),
    ],
    payload,
  })

  if (isLoading || templatesLoading) return <Skeleton variant="rounded" height={120} />

  const rows = items.map(toFindingsListRow)

  if (rows.length === 0) {
    return (
      <Stack py={8}>
        <EmptyState
          iconComponent={ListOutlined}
          title={formatMessage({ id: 'findings.empty.findings' })}
          variant={EmptyStateVariant.Small}
        />
      </Stack>
    )
  }

  return (
    <Stack spacing={1.5}>
      {rows.map((row, index) => (
        <FindingCard key={row.responseItemId ?? index} row={row} screeningTemplates={screeningTemplates} />
      ))}
    </Stack>
  )
}

const IntegrityScreeningSection: React.FC<IntegrityScreeningSectionProps> = ({
  providerId,
  screenings,
  benchmark,
  isLoading,
  isScreening = false,
}) => {
  const { formatMessage } = useIntl()
  const [selectedType, setSelectedType] = useState<AdverseScreeningQuestionnaire | undefined>()

  const toggleType = (type: AdverseScreeningQuestionnaire) =>
    setSelectedType(prev => (prev === type ? undefined : type))

  return (
    <Stack id={INTEGRITY_SECTION_ID} spacing={2} pt={2} sx={{ scrollMarginTop: theme => theme.spacing(2) }}>
      <Stack spacing={0.5}>
        <Typography variant="h6" fontWeight={600}>
          {formatMessage({ id: 'sustainabilityProfile.integrity.title' })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: 'sustainabilityProfile.integrity.description' })}
        </Typography>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {isLoading
          ? [0, 1, 2].map(key => <Skeleton key={key} variant="rounded" height={120} width="100%" />)
          : screenings.map(result => (
              <ScreeningCard
                key={result.column.type}
                result={result}
                benchmark={benchmark}
                selected={selectedType === result.column.type}
                onSelect={result.count > 0 ? () => toggleType(result.column.type) : undefined}
                isScreening={isScreening}
              />
            ))}
      </Stack>

      <FindingsList providerId={providerId} selectedType={selectedType} />
    </Stack>
  )
}

export default IntegrityScreeningSection
